import { h } from 'preact';
import { Text } from 'preact-i18n';

import { isActiveStatus } from '../../lib/smsBulk';

export const DEFAULT_SENDER = 'RaskePoter';

/*
 * Markup shared by the two utsendelse drawers: sendBulkSms.js (everyone in the
 * Besøkende list) and sendCalendarDaySms.js (the appointments of one day).
 * Pure presentational components: state and handlers stay in the drawers.
 */

export function SmsBulkSenderLine({ customer = {}, isEmail = false }) {
    if (isEmail) {
        return (
            <div class='small text-body-secondary mb-1'>
                <Text id='dyrejournal.sms-bulk-replyto'>Replies go to</Text>:{' '}
                {customer.emailSender
                    ? <strong>{customer.emailSender}</strong>
                    : <Text id='dyrejournal.sms-bulk-replyto-missing'>(no clinic e-mail set)</Text>}
            </div>
        );
    }
    return (
        <div class='small text-body-secondary mb-1'>
            <Text id='dyrejournal.sms-bulk-sender'>Sender</Text>: <strong>{customer.smsSender || DEFAULT_SENDER}</strong>
        </div>
    );
}

export function SmsBulkTemplates({
    textTemplates = [], showTemplates = false, onToggle, onApply,
}) {
    if (textTemplates.length === 0) {
        return null;
    }
    return (
        <div class='mb-2'>
            <button type='button' class='btn btn-sm btn-outline-primary rounded-pill px-3' onClick={onToggle}>
                <i class='fa-duotone fa-text-size me-2' />
                <Text id='input.choose-text-template'>Choose text template</Text>
                <span class='badge bg-primary ms-2'>{textTemplates.length}</span>
            </button>
            {showTemplates && <div class='list-group mt-2' style='max-height: 200px; overflow-y: auto;'>
                {textTemplates.map(tpl => (
                    <button key={tpl.id || tpl.title} type='button' class='list-group-item list-group-item-action text-start' data-body={tpl.body} onClick={onApply}>
                        {tpl.title && <strong class='d-block'>{tpl.title}</strong>}
                        <small class='text-body-secondary'>{(tpl.body || '').slice(0, 120)}</small>
                    </button>
                ))}
            </div>}
        </div>
    );
}

export function SmsBulkTestButton({
    user = {}, isEmail = false, body = '', testInProgress = false, testSent = false, onSend,
}) {
    const target = isEmail ? user.email : user.cellphone;
    return (
        <div class='mt-3 d-flex align-items-center flex-wrap gap-2'>
            <button
                type='button'
                class='btn btn-sm btn-outline-secondary rounded-pill'
                disabled={!target || !body.trim() || testInProgress}
                onClick={onSend}
            >
                <i class={`fa-duotone ${testInProgress ? 'fa-spinner fa-spin' : 'fa-paper-plane'} me-1`} />
                {isEmail
                    ? <Text id='dyrejournal.sms-bulk-test-email' fields={{ email: user.email || '' }}>Send test e-mail to me ({user.email})</Text>
                    : <Text id='dyrejournal.sms-bulk-test' fields={{ cellphone: user.cellphone || '' }}>Send test SMS to me ({user.cellphone})</Text>}
            </button>
            {testSent && <span class='text-success small'>
                <i class='fa-solid fa-check me-1' />
                <Text id='dyrejournal.sms-bulk-test-sent'>Test SMS sent</Text>
            </span>}
            {!target && <span class='text-body-secondary small'>
                {isEmail
                    ? <Text id='dyrejournal.sms-bulk-test-no-email'>Add an e-mail address on your profile to send a test.</Text>
                    : <Text id='dyrejournal.sms-bulk-test-no-mobile'>Add a mobile number on your profile to send a test.</Text>}
            </span>}
        </div>
    );
}

export function SmsBulkProgress({ job, onCancel, onClose }) {
    if (!job) {
        return null;
    }
    const handled = (job.sentCount || 0) + (job.failedCount || 0);
    const total = job.total || 0;
    const percent = total > 0 ? Math.round((handled / total) * 100) : 0;
    const active = isActiveStatus(job.status);
    return (<>
        <div class='d-flex justify-content-between align-items-baseline mb-1'>
            <strong>
                {active && <>
                    <i class='fa-duotone fa-spinner fa-spin me-1' />
                    <Text id='dyrejournal.sms-bulk-status-sending'>Sending...</Text>
                </>}
                {job.status === 'done' && <>
                    <i class='fa-solid fa-check text-success me-1' />
                    <Text id='dyrejournal.sms-bulk-status-done'>Finished</Text>
                </>}
                {job.status === 'cancelled' && <>
                    <i class='fa-solid fa-ban text-warning me-1' />
                    <Text id='dyrejournal.sms-bulk-status-cancelled'>Cancelled</Text>
                </>}
                {job.status === 'failed' && <>
                    <i class='fa-solid fa-triangle-exclamation text-danger me-1' />
                    <Text id='dyrejournal.sms-bulk-status-failed'>Stopped after repeated errors</Text>
                </>}
            </strong>
            <span class='text-body-secondary small'>{handled} / {total}</span>
        </div>
        <div class='progress mb-3' style='height: 10px;'>
            <div class={`progress-bar ${active ? 'progress-bar-striped progress-bar-animated' : ''}`} style={`width: ${percent}%`} />
        </div>
        <div class='small text-body-secondary mb-3'>
            <Text id='dyrejournal.sms-bulk-progress' fields={{ sent: job.sentCount || 0, failed: job.failedCount || 0 }}>{job.sentCount || 0} sent, {job.failedCount || 0} failed</Text>
            {job.lastError && <div class='text-danger'>{job.lastError}</div>}
            {active && <div>
                <Text id='dyrejournal.sms-bulk-background-hint'>Sending continues in the background. You can close this window.</Text>
            </div>}
        </div>
        <div class='d-flex justify-content-end gap-2'>
            {active && <button type='button' class='btn btn-outline-danger rounded-pill' onClick={onCancel}>
                <i class='fa-solid fa-ban me-1' />
                <Text id='dyrejournal.sms-bulk-cancel-job'>Stop sending</Text>
            </button>}
            <button type='button' class='btn btn-primary rounded-pill' onClick={onClose}>
                <Text id='dyrejournal.sms-bulk-close'>Close</Text>
            </button>
        </div>
    </>);
}
